import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { RunState, describeRun, fmtTime, money, num } from "./meta";

function UsageCell({ u }) {
  if (!u) return <span className="text-slate-400">—</span>;
  const c = u.counts;
  return (
    <span className="text-xs">
      <b className="font-mono tabular-nums">{money(u.total)}</b> · {num(u.domains)} domains
      <span className="block text-[11px] text-slate-500">
        {u.mode === "live" ? `${num(c.updated)} updated` : `${num(c.would_update)} would change`} · {num(c.unchanged)} right{c.failed ? ` · ${num(c.failed)} failed` : ""}
        <span className={`ml-1 rounded px-1 text-[10px] font-semibold ${u.mode === "live" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{u.mode === "live" ? "LIVE" : "DRY"}</span>
      </span>
    </span>
  );
}

export function RunsList() {
  const [runs, setRuns] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/usage-charges/runs").then(({ data }) => setRuns(data.runs)).catch(() => setRuns([])); }, []);
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="uc-runs">
      <div className="border-b border-slate-100 px-5 py-3"><h3 className="font-heading text-sm font-semibold text-slate-800">Run history</h3></div>
      {!runs ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-300" /> : !runs.length ? (
        <p className="px-5 py-6 text-sm text-slate-500" data-testid="uc-runs-empty">No runs yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-5 py-2 font-semibold">Started</th><th className="px-3 py-2 font-semibold">What</th>
              <th className="px-3 py-2 font-semibold">Calls collected</th><th className="px-3 py-2 font-semibold">Usage</th><th className="px-3 py-2 font-semibold">Status</th></tr></thead>
            <tbody>{runs.map((r) => (
              <tr key={r.id} onClick={() => nav(`runs/${r.id}`)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid={`uc-run-row-${r.id}`}>
                <td className="px-5 py-2.5 align-top text-xs"><div className="font-medium text-slate-800">{fmtTime(r.started_at)}</div>
                  <div className="text-slate-500">{r.trigger === "daily" ? "Daily schedule" : r.started_by_name}</div></td>
                <td className="px-3 py-2.5 align-top text-xs text-slate-600">{describeRun(r.params)}</td>
                <td className="px-3 py-2.5 align-top text-xs">{r.collect ? (<>
                  <b className="tabular-nums">{num(r.params.check_only ? r.collect.new : r.collect.inserted)}</b> {r.params.check_only ? "would be added" : "added"} · {num(r.collect.duplicates)} already saved
                  {(r.collect.missing_files?.length > 0 || r.collect.errors > 0) && <span className="block text-[11px] text-amber-700">{r.collect.errors ? `${r.collect.errors} day(s) failed · ` : ""}{r.collect.missing_files?.length ? `${r.collect.missing_files.length} WebCDR file(s) missing` : ""}</span>}
                </>) : <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2.5 align-top"><UsageCell u={r.usage} /></td>
                <td className="px-3 py-2.5 align-top"><RunState run={r} />{r.error && <div className="mt-1 max-w-[220px] truncate text-[11px] text-rose-700" title={r.error}>{r.error}</div>}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
